/**
 * Format API datetimes that are already merchant-local wall clocks.
 *
 * Work-period detail / employee ClockIn/ClockOut (and similar) are converted
 * server-side via ToLocal / ToMerchantLocal and serialized WITHOUT a timezone
 * (e.g. "2026-07-29T11:50:00"). Treating those as UTC (appending Z) then
 * formatting with toLocale* double-shifts the display.
 *
 * - No explicit TZ → print the wall-clock parts as-is (merchant local).
 * - Explicit Z / offset → real instant; format in the browser timezone
 *   (or optional timeZoneId when provided).
 */

const TZ_SUFFIX_RE = /(Z|[+-]\d{2}:?\d{2})$/i;
const WALL_RE = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2})(?:\.\d+)?)?)?/;

export function hasExplicitTimeZone(raw) {
  if (raw == null) return false;
  return TZ_SUFFIX_RE.test(String(raw).trim());
}

/** @returns {{ y: number, mo: number, d: number, h: number, mi: number, s: number } | null} */
export function parseWallClockParts(raw) {
  if (raw == null || raw === '') return null;
  const str = String(raw).trim();
  const m = str.match(WALL_RE);
  if (!m) return null;
  return {
    y: Number(m[1]),
    mo: Number(m[2]),
    d: Number(m[3]),
    h: Number(m[4] || 0),
    mi: Number(m[5] || 0),
    s: Number(m[6] || 0),
  };
}

function pad2(n) {
  return String(n).padStart(2, '0');
}

export function formatTime12FromParts(h, mi) {
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 || 12;
  return `${h12}:${pad2(mi)} ${ampm}`;
}

const WEEKDAYS_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const WEEKDAYS_LONG = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const MONTHS_LONG = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];
const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function weekdayIndex(y, mo, d) {
  // Noon UTC avoids DST edge cases when deriving weekday from a calendar date.
  return new Date(Date.UTC(y, mo - 1, d, 12, 0, 0)).getUTCDay();
}

/**
 * Parse an API datetime for duration math.
 * Wall-clock (no TZ) → Date constructed from components in the *runtime* local zone
 * (same zone as the merchant when viewing on-site; avoids false UTC shift).
 * Explicit TZ → real Date instant.
 */
export function parseApiDateTime(raw) {
  if (raw == null || raw === '') return null;
  const str = String(raw).trim();
  if (hasExplicitTimeZone(str)) {
    const d = new Date(str);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const p = parseWallClockParts(str);
  if (!p) {
    const d = new Date(str);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const d = new Date(p.y, p.mo - 1, p.d, p.h, p.mi, p.s);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * Calendar Date (local midnight) from API merchant-local timestamp or date-only.
 * Does not treat bare ISO datetimes as UTC.
 */
export function calendarDateFromApi(raw) {
  if (raw == null || raw === '') return null;
  const str = String(raw).trim();
  const dateOnly = str.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (dateOnly || /T00:00:00/.test(str)) {
    const m = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!m) return null;
    const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    return Number.isNaN(d.getTime()) ? null : d;
  }
  if (hasExplicitTimeZone(str)) {
    const instant = new Date(str);
    if (Number.isNaN(instant.getTime())) return null;
    return new Date(instant.getFullYear(), instant.getMonth(), instant.getDate());
  }
  const p = parseWallClockParts(str);
  if (!p) return null;
  const d = new Date(p.y, p.mo - 1, p.d);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** h:mm a — wall clock as-is when API omitted TZ. */
export function formatApiTime(raw, empty = '—') {
  if (raw == null || raw === '') return empty;
  const str = String(raw).trim();
  if (hasExplicitTimeZone(str)) {
    const d = new Date(str);
    if (Number.isNaN(d.getTime())) return empty;
    return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  }
  const p = parseWallClockParts(str);
  if (!p) return empty;
  return formatTime12FromParts(p.h, p.mi);
}

/** e.g. Wed, 7/29/26, 11:50 AM */
export function formatApiDateTime(raw, empty = '—') {
  if (raw == null || raw === '') return empty;
  const str = String(raw).trim();
  if (hasExplicitTimeZone(str)) {
    const d = new Date(str);
    if (Number.isNaN(d.getTime())) return empty;
    return d.toLocaleString('en-US', {
      weekday: 'short',
      month: 'numeric',
      day: 'numeric',
      year: '2-digit',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  }
  const p = parseWallClockParts(str);
  if (!p) return empty;
  const wd = WEEKDAYS_SHORT[weekdayIndex(p.y, p.mo, p.d)];
  const yy = String(p.y).slice(-2);
  return `${wd}, ${p.mo}/${p.d}/${yy}, ${formatTime12FromParts(p.h, p.mi)}`;
}

/** e.g. Wednesday, July 29, 2026 */
export function formatApiDateLong(raw, empty = '') {
  const cal = calendarDateFromApi(raw);
  if (!cal) return empty;
  const wd = WEEKDAYS_LONG[cal.getDay()];
  const month = MONTHS_LONG[cal.getMonth()];
  return `${wd}, ${month} ${cal.getDate()}, ${cal.getFullYear()}`;
}

/** e.g. Wed. 7/29/26 */
export function formatApiDateShort(raw, empty = '') {
  const cal = calendarDateFromApi(raw);
  if (!cal) return empty;
  const wd = WEEKDAYS_SHORT[cal.getDay()];
  const yy = String(cal.getFullYear()).slice(-2);
  return `${wd}. ${cal.getMonth() + 1}/${cal.getDate()}/${yy}`;
}

/**
 * Clock-out: time only, or "Wed, Jul 29, 11:50 AM" when more than 24h after clock-in.
 */
export function formatApiClockOut(clockInRaw, clockOutRaw, empty = '-') {
  if (clockOutRaw == null || String(clockOutRaw).trim() === '') return empty;
  const outStr = String(clockOutRaw).trim();
  if (outStr === '0001-01-01T00:00:00') return empty;

  const inMs = parseApiDateTime(clockInRaw)?.getTime();
  const outMs = parseApiDateTime(clockOutRaw)?.getTime();
  const spansMoreThanDay = inMs != null && outMs != null && (outMs - inMs) > 24 * 60 * 60 * 1000;

  if (!spansMoreThanDay) {
    return formatApiTime(clockOutRaw, empty);
  }

  if (hasExplicitTimeZone(outStr)) {
    const d = new Date(outStr);
    if (Number.isNaN(d.getTime())) return empty;
    const datePart = d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
    const timePart = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
    return `${datePart}, ${timePart}`;
  }

  const p = parseWallClockParts(outStr);
  if (!p) return empty;
  const wd = WEEKDAYS_SHORT[weekdayIndex(p.y, p.mo, p.d)];
  const mon = MONTHS_SHORT[p.mo - 1];
  return `${wd}, ${mon} ${p.d}, ${formatTime12FromParts(p.h, p.mi)}`;
}

/** YYYY-MM-DD from merchant-local API datetime (for navigation state). */
export function apiDateToYmd(raw) {
  if (hasExplicitTimeZone(raw)) {
    const d = parseApiDateTime(raw);
    if (!d) return null;
    return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
  }
  const p = parseWallClockParts(raw);
  if (!p) return null;
  return `${p.y}-${pad2(p.mo)}-${pad2(p.d)}`;
}
